import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Gift, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { BACKEND_URL, resolveAssetUrl } from '../config';
import { trackInstantRedeem } from '../utils/analytics';
import InstantRedeemSuccess from '../components/InstantRedeemSuccess';
import NewUserOnboarding from '../components/NewUserOnboarding';

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    red: '#ef4444',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
    borderDefault: 'rgba(30, 27, 75, 0.1)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

const InstantRedeem = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, openAuthModal } = useAuth();
  
  const businessId = searchParams.get('business_id');
  
  // States
  const [state, setState] = useState('loading');
  const [businessInfo, setBusinessInfo] = useState(null);
  const [dealInfo, setDealInfo] = useState(null);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Refs to prevent double-calls
  const hasInitialized = useRef(false);
  const isProcessing = useRef(false);

  // Process instant redemption
  const processInstantRedeem = useCallback(async () => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    
    setState('processing');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/instant-redeem/process`, {
        business_id: businessId
      });
      
      const data = response.data;
      
      if (!data.success) {
        setState('error');
        setError(data.error);
        return;
      }
      
      setRedemptionResult(data);
      trackInstantRedeem({ businessId, success: true });

      // Check if this is a new user signup (for onboarding)
      const isNewUser = localStorage.getItem('tappd_new_signup') === 'true';
      if (isNewUser) {
        localStorage.removeItem('tappd_new_signup');
        setState('onboarding');
      } else {
        setState('success');
      }
    } catch (err) {
      console.error('Error processing instant redeem:', err);
      setState('error');
      setError(err.response?.data?.detail || 'Failed to process redemption');
    }
  }, [businessId]);

  // Fetch business info and determine next state
  const fetchBusinessInfo = useCallback(async () => {
    if (!businessId) {
      setState('error');
      setError('No business specified');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/instant-redeem/info/${businessId}`);
      const data = response.data;

      if (data.error) {
        setState('error');
        setError(data.error);
        return;
      }

      setBusinessInfo(data.business);
      setDealInfo(data.deal);

      if (!data.is_authenticated) {
        setState('unauthenticated');
      } else if (!data.has_venue_unlocked) {
        setState('no-venue-unlocked');
      } else {
        // User is authenticated and has venue unlocked - process redemption
        processInstantRedeem();
      }
    } catch (err) {
      console.error('Error fetching business info:', err);
      setState('error');
      setError(err.response?.data?.detail || 'Failed to load business information');
    }
  }, [businessId, processInstantRedeem]);

  // Handle login/signup
  const handleAuth = (mode) => {
    localStorage.setItem('pending_instant_redeem', businessId);
    if (mode === 'signup') {
      localStorage.setItem('tappd_new_signup', 'true');
    }
    openAuthModal(mode);
  };

  // Initialize on mount
  useEffect(() => {
    if (authLoading) return;
    if (hasInitialized.current) return;
    
    const pendingRedeem = localStorage.getItem('pending_instant_redeem');
    
    if (pendingRedeem === businessId) {
      localStorage.removeItem('pending_instant_redeem');
    }
    
    hasInitialized.current = true;
    fetchBusinessInfo();
  }, [authLoading, businessId, fetchBusinessInfo]);

  // Re-fetch when authentication state changes (user logs in while on this page)
  useEffect(() => {
    if (authLoading) return;
    
    // If user just authenticated while we were showing unauthenticated state, re-fetch
    if (isAuthenticated && state === 'unauthenticated') {
      isProcessing.current = false; // Reset processing flag
      fetchBusinessInfo();
    }
  }, [isAuthenticated, authLoading, state, fetchBusinessInfo]);

  // Handle completing onboarding
  const handleOnboardingComplete = () => {
    setState('success');
  };

  // Render loading state
  if (state === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: v5.colors.purple }} />
          <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>Loading...</p>
        </motion.div>
      </div>
    );
  }

  // Render error state
  if (state === 'error') {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: v5.colors.cream }}>
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <img src="/tappd-logo.png" alt="Tappd" className="mx-auto mb-8 h-12" />
            
            <div 
              className="rounded-xl p-8"
              style={{ 
                backgroundColor: v5.colors.white,
                border: `1px solid ${v5.colors.borderLight}`,
                borderLeft: `4px solid ${v5.colors.red}`
              }}
            >
              <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: v5.colors.red }} />
              <h2 style={{ fontFamily: v5.fonts.display, fontSize: '24px', color: v5.colors.textPrimary, marginBottom: '12px' }}>
                Oops!
              </h2>
              <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted, marginBottom: '24px' }}>
                {error}
              </p>
              <Button
                onClick={() => navigate('/local-deals-near-me')}
                className="rounded-xl"
                style={{
                  background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                  color: v5.colors.white
                }}
              >
                Explore Other Deals
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render unauthenticated state
  if (state === 'unauthenticated') {
    return (
      <div className="min-h-screen py-12 px-4" style={{ backgroundColor: v5.colors.cream }}>
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <img src="/tappd-logo.png" alt="Tappd" className="mx-auto mb-8 h-12" />
            
            <div 
              className="rounded-xl p-8"
              style={{ 
                backgroundColor: v5.colors.white,
                border: `1px solid ${v5.colors.borderLight}`,
                borderLeft: `4px solid ${v5.colors.purple}`
              }}
            >
              {businessInfo?.logo_url && (
                <img 
                  src={resolveAssetUrl(businessInfo.logo_url)}
                  alt={businessInfo.name}
                  className="w-20 h-20 rounded-xl mx-auto mb-4 object-cover"
                />
              )}
              
              <p style={{ fontFamily: v5.fonts.mono, fontSize: '12px', color: v5.colors.textHint, marginBottom: '16px' }}>
                // instant_redeem
              </p>
              
              <h1 style={{ fontFamily: v5.fonts.display, fontSize: '28px', fontWeight: 500, color: v5.colors.textPrimary, marginBottom: '8px' }}>
                {businessInfo?.name}
              </h1>
              
              <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginBottom: '24px' }}>
                {businessInfo?.category}
              </p>
              
              {dealInfo && (
                <div 
                  className="rounded-lg p-4 mb-6"
                  style={{ backgroundColor: v5.colors.cream }}
                >
                  <Gift className="w-8 h-8 mx-auto mb-2" style={{ color: v5.colors.purple }} />
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '16px', fontWeight: 600, color: v5.colors.textPrimary }}>
                    {dealInfo.title}
                  </p>
                  <p style={{ fontFamily: v5.fonts.body, fontSize: '14px', color: v5.colors.textMuted, marginTop: '4px' }}>
                    {dealInfo.description}
                  </p>
                </div>
              )}
              
              <p style={{ fontFamily: v5.fonts.body, fontSize: '16px', color: v5.colors.textSecondary, marginBottom: '24px' }}>
                Login or sign up to claim your exclusive deal!
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => handleAuth('login')}
                  className="w-full rounded-xl py-6 text-base font-semibold"
                  style={{
                    background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                    color: v5.colors.white,
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  Login
                </Button>
                
                <Button
                  onClick={() => handleAuth('signup')}
                  variant="outline"
                  className="w-full rounded-xl py-6 text-base font-semibold"
                  style={{
                    border: `2px solid ${v5.colors.purple}`,
                    color: v5.colors.purple,
                    backgroundColor: 'transparent'
                  }}
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render no-venue-unlocked state - show full onboarding for new users
  if (state === 'no-venue-unlocked') {
    return (
      <NewUserOnboarding 
        businessName={businessInfo?.name}
        onComplete={() => navigate('/local-deals-near-me')}
        showSuccessFirst={false}
      />
    );
  }

  // Render processing state
  if (state === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: v5.colors.cream }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
          >
            <Sparkles className="w-10 h-10" style={{ color: v5.colors.purple }} />
          </motion.div>
          <h2 style={{ fontFamily: v5.fonts.display, fontSize: '24px', color: v5.colors.textPrimary, marginBottom: '8px' }}>
            Processing Your Deal...
          </h2>
          <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>
            Just a moment
          </p>
        </motion.div>
      </div>
    );
  }

  // Render onboarding state (for new signups)
  if (state === 'onboarding' && redemptionResult) {
    return (
      <NewUserOnboarding 
        redemptionResult={redemptionResult}
        onComplete={handleOnboardingComplete}
        showSuccessFirst={true}
      />
    );
  }

  // Render success state
  if (state === 'success' && redemptionResult) {
    return (
      <InstantRedeemSuccess 
        result={redemptionResult}
        onExploreDeals={() => navigate('/local-deals-near-me')}
        onViewRewards={() => navigate('/rewards')}
      />
    );
  }

  return null;
};

export default InstantRedeem;
